import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import {
  externalCompleteDelivery,
  externalDriverResponse,
  externalStartDelivery,
  getExternalDeliveryRequestById,
} from "../../api/driverPublicApi";
import {
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaUser,
  FaCheckCircle,
  FaTimesCircle,
  FaInfoCircle,
  FaStickyNote,
  FaSpinner
} from "react-icons/fa";

const apiErrorMessage = (error, fallback) => (
  error?.response?.data?.error
  || error?.response?.data?.message
  || error?.message
  || fallback
);

export default function DeliveryDetail() {
  const { deliveryRequestId: requestId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [deliveryData, setDeliveryData] = useState(null);

  // Modals / Action Prompts
  const [showDeclineReason, setShowDeclineReason] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [showCompleteRemarks, setShowCompleteRemarks] = useState(false);
  const [remarks, setRemarks] = useState("");

  // Toast State
  const [toast, setToast] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchDeliveryDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getExternalDeliveryRequestById(requestId);

      const resData = response.data?.data || response.data;
      if (!resData) {
        throw new Error("No data found for this delivery job.");
      }
      setDeliveryData(resData);
    } catch (err) {
      console.error("Error fetching delivery details:", err);
      setError(apiErrorMessage(err, "Failed to load delivery details. Please check the link or try again."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (requestId?.trim()) {
      fetchDeliveryDetails();
    } else {
      setError("Invalid or incomplete delivery link.");
      setLoading(false);
    }
  }, [requestId]);

  // Extract variables with fallbacks to handle potential API variations
  const data = deliveryData || {};
  const invitationId = data.invitation_id
    ?? data.invitations?.find((invitation) => String(invitation.status || "").toLowerCase() === "pending")?.id
    ?? data.invitations?.[0]?.id;
  const canRespondToInvitation = Number.isInteger(Number(invitationId)) && Number(invitationId) > 0;
  const deliveryRequestId = data.delivery_request_id || data.id || "N/A";
  const driverName = data.driver_name || data.driver?.name || "Driver";
  const rawStatus = (data.status || data.delivery_status || "").trim();
  const orderId = data.order_id || data.order_code || data.booking_id || "N/A";
  const careProName = data.care_pro_name || data.caregiver_name || data.carepro?.name || "N/A";
  const careProMobile = data.care_pro_mobile || data.caregiver_mobile || data.carepro?.mobile || "";
  const pickupLocation = data.pickup_location || data.pickup_address || data.pickup || "N/A";
  const destination = data.destination || data.destination_location || data.destination_address || "N/A";
  const scheduledTime = data.scheduled_time || data.scheduled_at || data.start_time || data.scheduled_pickup_datetime;
  const estimatedDuration = data.estimated_duration || data.duration || data.estimated_duration_minutes;
  const specialNotes = data.special_notes || data.notes || data.remarks || "";
  const actualStartTime = data.actual_start_time || data.actual_start || data.started_at || data.actual_start_at;
  const actualEndTime = data.actual_end_time || data.actual_end || data.completed_at || data.actual_end_at;

  // In progress check
  const hasStarted = !!actualStartTime;

  // Classify active status workflow case
  let statusCase = "Open"; // Default fallback
  const normalizedStatus = rawStatus.toLowerCase();

  if (normalizedStatus === "completed" || normalizedStatus === "cancelled" || normalizedStatus === "rejected" || normalizedStatus === "declined") {
    statusCase = "CompletedOrCancelled";
  } else if (normalizedStatus === "accepted") {
    statusCase = hasStarted ? "AcceptedStarted" : "AcceptedNotStarted";
  } else {
    statusCase = "Open"; // Pending/Invitation status
  }

  // Format times nicely using moment
  const formatDateTime = (timeStr) => {
    if (!timeStr) return "N/A";
    const parsed = moment(timeStr);
    return parsed.isValid() ? parsed.format("DD MMM YYYY, hh:mm A") : timeStr;
  };

  // Get status color / text styling
  const getStatusBadge = () => {
    switch (statusCase) {
      case "AcceptedNotStarted":
        return {
          text: "Accepted (Pending Start)",
          className: "bg-blue-50 text-blue-700 border border-blue-200"
        };
      case "AcceptedStarted":
        return {
          text: "In Progress",
          className: "bg-indigo-50 text-indigo-700 border border-indigo-200 animate-pulse"
        };
      case "CompletedOrCancelled":
        if (normalizedStatus === "completed") {
          return {
            text: "Completed",
            className: "bg-emerald-50 text-emerald-700 border border-emerald-200"
          };
        } else {
          return {
            text: normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1),
            className: "bg-rose-50 text-rose-700 border border-rose-200"
          };
        }
      case "Open":
      default:
        return {
          text: "Pending Confirmation",
          className: "bg-amber-50 text-amber-700 border border-amber-200"
        };
    }
  };

  const statusBadge = getStatusBadge();

  // Workflow Handlers
  const handleAccept = async () => {
    if (!canRespondToInvitation) {
      showToast("No active driver invitation was found for this delivery job.", "error");
      return;
    }
    setSubmitting(true);

    try {
      const response = await externalDriverResponse(requestId, "Accepted", "");
      if (response.status === 200 || response.data?.success) {
        showToast("Delivery job confirmed and accepted successfully!");
        fetchDeliveryDetails();
      } else {
        throw new Error(response.data?.message || "Failed to accept the job.");
      }
    } catch (err) {
      showToast(apiErrorMessage(err, "Something went wrong. Please try again."), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async (e) => {
    e.preventDefault();
    if (!canRespondToInvitation) {
      showToast("No active driver invitation was found for this delivery job.", "error");
      return;
    }
    if (!declineReason.trim()) {
      showToast("Please provide a reason for declining the job.", "error");
      return;
    }
    setSubmitting(true);

    try {
      const response = await externalDriverResponse(requestId, "Rejected", declineReason.trim());
      if (response.status === 200 || response.data?.success) {
        showToast("Delivery job declined.");
        setShowDeclineReason(false);
        fetchDeliveryDetails();
      } else {
        throw new Error(response.data?.message || "Failed to decline the job.");
      }
    } catch (err) {
      showToast(apiErrorMessage(err, "Something went wrong. Please try again."), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartTrip = async () => {
    setSubmitting(true);

    try {
      const response = await externalStartDelivery(requestId);
      if (response.status === 200 || response.data?.success) {
        showToast("Trip started successfully! Drive safe.");
        fetchDeliveryDetails();
      } else {
        throw new Error(response.data?.message || "Failed to start the trip.");
      }
    } catch (err) {
      showToast(apiErrorMessage(err, "Something went wrong. Please try again."), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteDelivery = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await externalCompleteDelivery(requestId, remarks.trim());
      if (response.status === 200 || response.data?.success) {
        showToast("Delivery completed successfully! Great job.");
        setShowCompleteRemarks(false);
        fetchDeliveryDetails();
      } else {
        throw new Error(response.data?.message || "Failed to complete the delivery.");
      }
    } catch (err) {
      showToast(apiErrorMessage(err, "Something went wrong. Please try again."), "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col justify-center items-center p-4">
        <FaSpinner className="animate-spin text-4xl text-purple-700 mb-4" />
        <p className="text-slate-600 font-medium animate-pulse">Loading delivery job details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col justify-center items-center p-4">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 max-w-md w-full text-center">
          <FaTimesCircle className="text-5xl text-rose-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Failed to Load Job</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <button
            onClick={fetchDeliveryDetails}
            className="w-full bg-purple-700 hover:bg-purple-800 active:bg-purple-950 text-white py-3 rounded-xl font-medium transition duration-250 shadow-sm"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">

      {/* Toast Alert overlay */}
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-11/12 max-w-sm">
          <div className={`p-4 rounded-xl shadow-lg border flex items-start gap-3 ${
            toast.type === "error"
              ? "bg-rose-50 text-rose-800 border-rose-200"
              : "bg-emerald-50 text-emerald-800 border-emerald-200"
          }`}>
            {toast.type === "error" ? (
              <FaTimesCircle className="text-xl shrink-0 mt-0.5" />
            ) : (
              <FaCheckCircle className="text-xl shrink-0 mt-0.5" />
            )}
            <div className="text-sm font-semibold">{toast.message}</div>
          </div>
        </div>
      )}

      <div className="py-6 px-4 sm:px-6 lg:px-8 flex-1">
        {/* Main Responsive Mobile-first Container */}
        <div className="max-w-md mx-auto flex flex-col gap-6">

          {/* Header Section */}
          <div className="text-center sm:text-left mt-2">
            <h1 className="text-2xl font-black tracking-tight text-slate-900 leading-snug">
              Hi {driverName},
            </h1>
            <p className="text-slate-600 text-sm mt-1">
              please confirm the delivery job details below.
            </p>
          </div>

          {/* Dynamic Status Banner */}
          <div className={`w-full flex items-center justify-between p-4 rounded-2xl ${statusBadge.className}`}>
            <div className="flex items-center gap-2">
              <FaInfoCircle className="text-lg" />
              <span className="text-sm font-bold tracking-wide">Status:</span>
            </div>
            <span className="text-xs font-black uppercase tracking-wider bg-white/60 px-3 py-1 rounded-full shadow-2xs">
              {statusBadge.text}
            </span>
          </div>

          {/* Delivery Details Card (Glassmorphic style) */}
          <div className="bg-white/80 backdrop-blur-md rounded-3xl p-6 shadow-sm border border-slate-100/60 flex flex-col gap-5">

            <div className="border-b border-slate-100 pb-3 flex justify-between items-center">
              <span className="text-xs font-extrabold text-slate-400 uppercase tracking-widest">
                Job Information
              </span>
              <span className="text-xs font-mono font-bold bg-slate-100 text-slate-700 px-2.5 py-1 rounded-md">
                #{deliveryRequestId}
              </span>
            </div>

          {/* Vertical Details List */}
          <div className="flex flex-col gap-4">

            {/* Order ID */}
            <div className="flex justify-between items-center gap-4">
              <span className="text-slate-500 text-xs font-bold">Order ID</span>
              <span className="text-slate-800 text-sm font-black">{orderId}</span>
            </div>

            {/* Care Pro Name & Phone */}
            <div className="bg-slate-50/50 rounded-2xl p-3 border border-slate-100/50 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="bg-purple-100 text-purple-700 p-2.5 rounded-xl">
                  <FaUser className="text-sm" />
                </div>
                <div className="flex flex-col">
                  <span className="text-slate-400 text-[10px] font-black uppercase tracking-wider">Care Pro</span>
                  <span className="text-slate-800 text-sm font-bold">{careProName}</span>
                </div>
              </div>
              {careProMobile && (
                <a
                  href={`tel:${careProMobile}`}
                  className="bg-white hover:bg-slate-50 active:bg-slate-100 text-purple-700 border border-slate-200 p-2.5 rounded-xl shadow-xs transition duration-200 flex items-center justify-center"
                  title="Call Care Pro"
                >
                  <FaPhoneAlt className="text-sm" />
                </a>
              )}
            </div>

            {/* Scheduled Time */}
            <div className="flex items-start gap-3">
              <FaCalendarAlt className="text-purple-600 mt-0.5 text-sm shrink-0" />
              <div className="flex flex-col">
                <span className="text-slate-400 text-[10px] font-black uppercase tracking-wider">Scheduled Time</span>
                <span className="text-slate-800 text-sm font-bold">{formatDateTime(scheduledTime)}</span>
              </div>
            </div>

            {/* Estimated Duration */}
            <div className="flex items-start gap-3">
              <FaClock className="text-purple-600 mt-0.5 text-sm shrink-0" />
              <div className="flex flex-col">
                <span className="text-slate-400 text-[10px] font-black uppercase tracking-wider">Est. Duration</span>
                <span className="text-slate-800 text-sm font-bold">
                  {estimatedDuration} {typeof estimatedDuration === "number" ? "minutes" : ""}
                </span>
              </div>
            </div>

            {/* Route locations */}
            <div className="grid grid-cols-2 gap-3" data-testid="route-locations">
              <div className="flex min-w-0 items-start gap-2 rounded-2xl border border-slate-100 bg-slate-50/50 p-3">
                <FaMapMarkerAlt className="text-rose-500 mt-0.5 text-sm shrink-0" />
                <div className="flex min-w-0 flex-col">
                  <span className="text-slate-400 text-[10px] font-black uppercase tracking-wider">Pickup From</span>
                  <span className="break-words text-slate-700 text-sm font-medium">{pickupLocation}</span>
                </div>
              </div>

              <div className="flex min-w-0 items-start gap-2 rounded-2xl border border-slate-100 bg-slate-50/50 p-3">
                <FaMapMarkerAlt className="text-emerald-500 mt-0.5 text-sm shrink-0" />
                <div className="flex min-w-0 flex-col">
                  <span className="text-slate-400 text-[10px] font-black uppercase tracking-wider">Deliver To</span>
                  <span className="break-words text-slate-700 text-sm font-medium">{destination}</span>
                </div>
              </div>
            </div>

            {/* Special Notes (if available) */}
            {specialNotes && (
              <div className="bg-amber-50/50 border border-amber-100 rounded-2xl p-4 flex gap-3 items-start mt-1">
                <FaStickyNote className="text-amber-600 text-sm shrink-0 mt-0.5" />
                <div className="flex flex-col gap-1">
                  <span className="text-amber-800 text-[10px] font-black uppercase tracking-wider">Special Notes</span>
                  <p className="text-amber-900 text-xs leading-relaxed font-medium">{specialNotes}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Rejection/Decline Reason Overlay Modal/Form */}
        {showDeclineReason && (
          <div className="bg-white rounded-3xl p-6 shadow-md border border-slate-100">
            <h3 className="text-base font-bold text-slate-900 mb-3 flex items-center gap-2">
              <FaTimesCircle className="text-rose-500" /> Decline Invitation
            </h3>
            <form onSubmit={handleDecline} className="flex flex-col gap-4">
              <div>
                <label className="block text-slate-600 text-xs font-bold mb-1.5" htmlFor="reason">
                  Please provide a reason for declining this delivery:
                </label>
                <textarea
                  id="reason"
                  rows="3"
                  className="w-full text-slate-800 text-sm border border-slate-200 rounded-xl p-3 outline-none focus:border-purple-600 focus:ring-1 focus:ring-purple-600"
                  placeholder="E.g., Vehicle issue, duplicate booking, fully booked..."
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  disabled={submitting}
                  required
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowDeclineReason(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-50 font-medium rounded-xl text-xs transition duration-200"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white font-semibold rounded-xl text-xs shadow-sm transition duration-200 flex items-center gap-1.5"
                  disabled={submitting}
                >
                  {submitting && <FaSpinner className="animate-spin text-xs" />} Submit Decline
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Complete Remarks Overlay Modal/Form */}
        {showCompleteRemarks && (
          <div className="bg-white rounded-3xl p-6 shadow-md border border-slate-100">
            <h3 className="text-base font-bold text-slate-900 mb-3 flex items-center gap-2">
              <FaCheckCircle className="text-emerald-500" /> Complete Delivery
            </h3>
            <form onSubmit={handleCompleteDelivery} className="flex flex-col gap-4">
              <div>
                <label className="block text-slate-600 text-xs font-bold mb-1.5" htmlFor="remarks">
                  Add optional remarks/handover notes:
                </label>
                <textarea
                  id="remarks"
                  rows="3"
                  className="w-full text-slate-800 text-sm border border-slate-200 rounded-xl p-3 outline-none focus:border-purple-600 focus:ring-1 focus:ring-purple-600"
                  placeholder="E.g., Delivered to receptionist, handed over to Care Pro..."
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  disabled={submitting}
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowCompleteRemarks(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-50 font-medium rounded-xl text-xs transition duration-200"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-semibold rounded-xl text-xs shadow-sm transition duration-200 flex items-center gap-1.5"
                  disabled={submitting}
                >
                  {submitting && <FaSpinner className="animate-spin text-xs" />} Complete Job
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Action Section (Card footer equivalent) */}
        {!showDeclineReason && !showCompleteRemarks && (
          <div className="flex flex-col gap-3">

            {/* Case A: Open Status */}
            {statusCase === "Open" && (
              canRespondToInvitation ? (
                <div className="flex flex-col gap-3">
                  <button
                    onClick={handleAccept}
                    disabled={submitting}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold py-4 px-6 rounded-2xl transition duration-200 flex justify-center items-center gap-2 shadow-sm disabled:opacity-50"
                  >
                    {submitting ? (
                      <FaSpinner className="animate-spin text-lg" />
                    ) : (
                      <>
                        <FaCheckCircle className="text-lg" />
                        <span>Confirm & Accept Job</span>
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => setShowDeclineReason(true)}
                    disabled={submitting}
                    className="w-full bg-white hover:bg-rose-50 hover:text-rose-700 text-slate-700 font-bold py-3.5 px-6 rounded-2xl border border-slate-200 hover:border-rose-200 transition duration-200 flex justify-center items-center gap-2 disabled:opacity-50"
                  >
                    <FaTimesCircle className="text-lg text-rose-500 shrink-0" />
                    <span>Decline Job</span>
                  </button>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-amber-900 text-sm font-medium">
                  No active driver invitation is attached to this delivery job. Please contact the care manager for a new invitation link.
                </div>
              )
            )}

            {/* Case B: Accepted (Not Started) */}
            {statusCase === "AcceptedNotStarted" && (
              <button
                onClick={handleStartTrip}
                disabled={submitting}
                className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-bold py-4 px-6 rounded-2xl transition duration-200 flex justify-center items-center gap-2 shadow-sm disabled:opacity-50"
              >
                {submitting ? (
                  <FaSpinner className="animate-spin text-lg" />
                ) : (
                  <>
                    <FaClock className="text-lg animate-pulse" />
                    <span>Start Trip</span>
                  </>
                )}
              </button>
            )}

            {/* Case C: Accepted (Started/In Progress) */}
            {statusCase === "AcceptedStarted" && (
              <button
                onClick={() => setShowCompleteRemarks(true)}
                disabled={submitting}
                className="w-full bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold py-4 px-6 rounded-2xl transition duration-200 flex justify-center items-center gap-2 shadow-sm disabled:opacity-50"
              >
                <FaCheckCircle className="text-lg" />
                <span>Complete Delivery</span>
              </button>
            )}

            {/* Case D: Completed / Cancelled / Rejected */}
            {statusCase === "CompletedOrCancelled" && (
              <div className="bg-slate-100 rounded-2xl p-5 border border-slate-200 flex flex-col gap-3">
                <div className="text-slate-400 text-[10px] font-black uppercase tracking-wider border-b border-slate-200 pb-1">
                  Trip Timestamps Summary
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 text-xs font-semibold">Actual Start:</span>
                    <span className="text-slate-800 text-xs font-mono font-bold">
                      {formatDateTime(actualStartTime)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 text-xs font-semibold">Actual End:</span>
                    <span className="text-slate-800 text-xs font-mono font-bold">
                      {formatDateTime(actualEndTime)}
                    </span>
                  </div>
                  {normalizedStatus === "rejected" && data.reason && (
                    <div className="mt-2 text-rose-700 text-xs font-medium bg-rose-50 border border-rose-100 p-2.5 rounded-xl">
                      <span className="font-extrabold block mb-0.5 text-[9px] uppercase tracking-wider">Refusal Reason:</span>
                      "{data.reason}"
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        )}

        </div>
      </div>
    </div>
  );
}
